import { useEffect, useState } from 'react';
import {
  POSSIBLE_COMPETITOR_ANALYSIS_PATH,
  PossibleCompetitorAnalysis,
  PossibleCompetitorAnalysisResponse,
  getPossibleCompetitorAnalysis,
  peekPossibleCompetitorAnalysis,
} from 'services/actions/ai_management_possible_competitors';
import { invalidateCache } from 'services/request_cache';

interface State {
  analysis: PossibleCompetitorAnalysis | null;
  pending: boolean;
  loading: boolean;
  error: boolean;
}

const loadingState: State = { analysis: null, pending: false, loading: true, error: false };
const errorState: State = { analysis: null, pending: false, loading: false, error: true };

const fromResponse = (data: PossibleCompetitorAnalysisResponse): State => ({
  analysis: data.analise ?? null,
  pending: Boolean(data.pendente),
  loading: false,
  error: false,
});

export function usePossibleCompetitorAnalysis(biddingId: number | null, cnpj: string) {
  const peek = () => (biddingId ? peekPossibleCompetitorAnalysis(biddingId, cnpj) : undefined);
  const [state, setState] = useState<State>(() => {
    const hit = peek();
    return hit ? fromResponse(hit.data) : loadingState;
  });

  useEffect(() => {
    if (!biddingId) {
      setState(errorState);
      return undefined;
    }

    const hit = peek();
    if (hit) {
      setState(fromResponse(hit.data));
      return undefined;
    }

    let active = true;
    setState(loadingState);

    getPossibleCompetitorAnalysis(biddingId, cnpj)
      .then(({ data }) => {
        if (data.pendente) {
          invalidateCache((url) => url.endsWith(POSSIBLE_COMPETITOR_ANALYSIS_PATH));
        }
        if (active) setState(fromResponse(data));
      })
      .catch(() => {
        if (active) setState(errorState);
      });

    return () => {
      active = false;
    };
  }, [biddingId, cnpj]);

  return state;
}
